import type { LucideIcon } from "lucide-react";
import {
	BanIcon,
	BotIcon,
	CircleAlertIcon,
	HeadsetIcon,
	ImageIcon,
	LanguagesIcon,
	LogOutIcon,
	MessageSquareIcon,
	MicIcon,
	PhoneIcon,
	RotateCcwIcon,
	ScanSearchIcon,
	SearchXIcon,
	ShieldAlertIcon,
} from "lucide-react";
import { z } from "zod";
import type {
	ConversationChannel,
	ConversationStatus,
	MessageInput,
} from "./types";

export const STATUSES = [
	"ACTIVE",
	"ESCALATED",
	"RESOLVED",
	"ABANDONED",
] as const satisfies readonly ConversationStatus[];

export const CHANNELS = [
	"WEB",
	"API",
	"WHATSAPP",
	"MCP",
	"PLAYGROUND",
] as const satisfies readonly ConversationChannel[];

export const INPUTS = [
	"TEXT",
	"VOICE",
	"CALL",
	"IMAGE",
] as const satisfies readonly MessageInput[];

/** How a turn was said. The inbox shows these per conversation and per message. */
export const INPUT_META: Record<
	MessageInput,
	{ label: string; description: string; icon: LucideIcon }
> = {
	TEXT: {
		label: "Chat",
		description: "Typed in the chat.",
		icon: MessageSquareIcon,
	},
	VOICE: {
		label: "Voice note",
		description: "Recorded, transcribed, then answered in writing.",
		icon: MicIcon,
	},
	CALL: {
		label: "Call",
		description: "Said out loud on a live call and answered out loud.",
		icon: PhoneIcon,
	},
	IMAGE: {
		label: "Photo",
		description:
			"A photo the shopper sent, read into a request and answered in writing.",
		icon: ImageIcon,
	},
};

/** "Chat", "Chat and call", "Chat, voice note and call". */
export function inputsLabel(inputs: readonly MessageInput[]): string {
	const used = new Set(inputs);
	const labels = INPUTS.filter((input) => used.has(input)).map((input) =>
		INPUT_META[input].label.toLowerCase(),
	);
	if (labels.length === 0) {
		return "Chat";
	}
	const sentence =
		labels.length === 1
			? labels[0]
			: `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
	return (sentence ?? "").replace(/^./, (c) => c.toUpperCase());
}

/** Every inbox filter lives in the URL so a triage view is linkable. */
export const conversationsSearchSchema = z.object({
	id: z.string().optional(),
	/** Needs a person: a handoff was asked for, or someone on the team holds it. */
	handoff: z.boolean().optional(),
	status: z.enum(STATUSES).optional(),
	channel: z.enum(CHANNELS).optional(),
	via: z.enum(INPUTS).optional(),
	flag: z.string().optional(),
	lang: z.string().optional(),
	store: z.string().optional(),
	/** One WhatsApp number (its connection id). */
	number: z.string().optional(),
	shopper: z.string().optional(),
	/** Coerced: the router reads a pasted ?q=5449000000996 (a barcode) as a number. */
	q: z.coerce.string().optional(),
});

export type ConversationsSearch = z.infer<typeof conversationsSearchSchema>;

/** The list query input for a URL state; shared by the loader and the screen so keys match. */
export function listInputFrom(
	organizationId: string,
	search: ConversationsSearch,
) {
	return {
		organizationId,
		...(search.handoff ? { handoff: true } : {}),
		...(search.status ? { status: search.status } : {}),
		...(search.channel ? { channel: search.channel } : {}),
		...(search.via ? { via: search.via } : {}),
		...(search.flag ? { flag: search.flag } : {}),
		...(search.lang ? { language: search.lang } : {}),
		...(search.store ? { storeId: search.store } : {}),
		...(search.number ? { connectionId: search.number } : {}),
		...(search.shopper ? { shopper: search.shopper } : {}),
		...(search.q ? { q: search.q } : {}),
	};
}

export const STATUS_META: Record<
	ConversationStatus,
	{
		label: string;
		/** Classes for a bespoke pill; prefer `variant` with the kit Badge. */
		tone: string;
		variant: "secondary" | "warning" | "success" | "outline";
	}
> = {
	ACTIVE: {
		label: "Open",
		tone: "bg-muted text-muted-foreground",
		variant: "secondary",
	},
	ESCALATED: {
		label: "Escalated",
		tone: "bg-warning/22 text-warning-foreground dark:bg-warning/18 dark:text-warning",
		variant: "warning",
	},
	RESOLVED: {
		label: "Resolved",
		tone: "bg-success/12 text-success",
		variant: "success",
	},
	ABANDONED: {
		label: "Abandoned",
		tone: "bg-muted text-muted-foreground",
		variant: "outline",
	},
};

export const CHANNEL_LABEL: Record<ConversationChannel, string> = {
	WEB: "Hosted chat",
	API: "Retailer app",
	PLAYGROUND: "Playground",
	EVAL: "Bench run",
	WHATSAPP: "WhatsApp",
	MCP: "AI assistant (MCP)",
};

interface FlagMeta {
	label: string;
	description: string;
	icon: LucideIcon;
	/** Red flags are the ones worth opening first. */
	severe: boolean;
}

const FLAG_META: Record<string, FlagMeta> = {
	refusal_without_search: {
		label: "Refused without searching",
		description:
			"The agent said something is not sold without running a search that turn.",
		icon: BanIcon,
		severe: true,
	},
	script_mismatch: {
		label: "Wrong script",
		description:
			"The shopper wrote Arabizi and the reply contains Arabic letters.",
		icon: LanguagesIcon,
		severe: true,
	},
	zero_results: {
		label: "Search found nothing",
		description:
			"At least one search or ingredient lookup returned no products.",
		icon: SearchXIcon,
		severe: false,
	},
	error: {
		label: "Turn failed",
		description: "A model or tool error interrupted a reply.",
		icon: CircleAlertIcon,
		severe: true,
	},
	escalated: {
		label: "Asked for a human",
		description: "The agent handed the conversation to the support team.",
		icon: HeadsetIcon,
		severe: true,
	},
	aborted: {
		label: "Left mid-reply",
		description: "The shopper closed the chat before a reply finished.",
		icon: LogOutIcon,
		severe: false,
	},
	validator_retry: {
		label: "Reply rewritten",
		description:
			"The output validator rejected a reply and the agent retried.",
		icon: RotateCcwIcon,
		severe: false,
	},
	automated: {
		label: "Automated",
		description:
			"Sent by a script or a bot: a script's browser, follow-ups faster than anyone can read, or one address opening many conversations in minutes.",
		icon: BotIcon,
		severe: false,
	},
	probing: {
		label: "Asked how it's built",
		description:
			"The visitor asked about the assistant itself: which AI or tools it uses, where its data comes from, or what it stores.",
		icon: ScanSearchIcon,
		severe: false,
	},
	age_restricted_requested: {
		label: "Age-restricted request",
		description: "The shopper asked for an age-restricted product.",
		icon: ShieldAlertIcon,
		severe: false,
	},
};

export function flagMeta(flag: string): FlagMeta {
	return (
		FLAG_META[flag] ?? {
			label: flag.replaceAll("_", " "),
			description: flag,
			icon: CircleAlertIcon,
			severe: false,
		}
	);
}

const usd = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 4,
});

/** Model spend is fractions of a cent per turn; show enough digits to compare. */
export function formatCost(value: string | number | null | undefined): string {
	const n = Number(value ?? 0);
	if (n === 0) {
		return "$0";
	}
	return n < 0.0001 ? "<$0.0001" : usd.format(n);
}

export function formatMs(ms: number | null | undefined): string {
	if (ms == null) {
		return "n/a";
	}
	return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function formatTokens(n: number | null | undefined): string {
	if (n == null) {
		return "0";
	}
	return n >= 10_000
		? `${(n / 1000).toFixed(1)}k`
		: n.toLocaleString("en-US");
}
